import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { shopifyApi } from '@/services/api';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import {
  Menu, Loader2, Download, Package, Search, Check, RefreshCw, ExternalLink,
} from 'lucide-react';

type ImportRow = {
  product_id: string;
  variant_id: string;
  inventory_item_id?: string;
  title: string;
  product_title?: string;
  variant_title?: string;
  sku?: string;
  barcode?: string;
  price: number;
  qty: number;
  category_hint?: string;
  image_url?: string;
  already_linked?: boolean;
  description?: string;
  product_type?: string;
  variant_count?: number;
  is_multi_variant?: boolean;
};

type Collection = { id: string; title: string; handle?: string };

export default function ShopifyImportPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();

  const [collectionId, setCollectionId] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [notInPos, setNotInPos] = useState(true);
  const [importCategories, setImportCategories] = useState(true);
  const [importStock, setImportStock] = useState(true);
  const [skipExisting, setSkipExisting] = useState(true);
  const [selected, setSelected] = useState<Record<string, ImportRow>>({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [resultMsg, setResultMsg] = useState('');

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['shopifyStatus'],
    queryFn: async () => {
      const res = await shopifyApi.getStatus();
      return res.data.message as {
        enable_shopify?: boolean;
        shopify_connection_status?: string;
        package_allows?: boolean;
      };
    },
  });

  const connected =
    status?.enable_shopify &&
    (status.shopify_connection_status || '').toLowerCase() === 'connected';

  const { data: collectionsData, isLoading: colsLoading } = useQuery({
    queryKey: ['shopifyImportCollections'],
    queryFn: async () => {
      const res = await shopifyApi.importPreviewCollections();
      return (res.data.message?.collections || []) as Collection[];
    },
    enabled: !!connected,
  });

  const {
    data: productsData,
    isLoading: productsLoading,
    isFetching,
    isError: productsError,
    error: productsErr,
    refetch,
  } = useQuery({
    queryKey: ['shopifyImportProducts', cursor, search, collectionId, notInPos],
    queryFn: async () => {
      const res = await shopifyApi.importPreviewProducts({
        cursor: cursor || undefined,
        page_size: 15,
        search: search || undefined,
        collection_id: collectionId || undefined,
        not_in_pos: notInPos ? 1 : 0,
      });
      return res.data.message as {
        rows: ImportRow[];
        page_info: { has_next_page?: boolean; end_cursor?: string | null };
      };
    },
    enabled: !!connected,
    retry: 1,
  });

  const rows = productsData?.rows || [];
  const pageInfo = productsData?.page_info || {};
  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selected[r.variant_id]);

  const toggleRow = (row: ImportRow) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.variant_id]) delete next[row.variant_id];
      else next[row.variant_id] = row;
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allOnPageSelected) {
        rows.forEach((r) => delete next[r.variant_id]);
      } else {
        rows.forEach((r) => {
          next[r.variant_id] = r;
        });
      }
      return next;
    });
  };

  const importMutation = useMutation({
    mutationFn: () =>
      shopifyApi.importApply({
        rows: selectedList,
        import_categories: importCategories ? 1 : 0,
        import_stock: importStock ? 1 : 0,
        skip_existing: skipExisting ? 1 : 0,
        background: selectedList.length > 50 ? 1 : 0,
      }),
    onSuccess: (res) => {
      const msg = res.data.message || {};
      if (msg.queued) {
        setResultMsg(msg.message || t('shopify.importQueued'));
        toast.success(msg.message || t('shopify.importQueued'));
      } else {
        const text = t('shopify.importResult', {
          created: msg.created ?? 0,
          updated: msg.updated ?? 0,
          skipped: msg.skipped ?? 0,
        });
        setResultMsg(text);
        toast.success(text);
        if (msg.errors?.length) {
          toast.error(t('shopify.importPartialErrors', { count: msg.errors.length }));
        }
      }
      setSelected({});
      void refetch();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err.message || t('shopify.importFailed'));
    },
  });

  const applySearch = () => {
    setCursor(null);
    setCursorStack([null]);
    setSearch(searchInput.trim());
  };

  const goNext = () => {
    if (!pageInfo.has_next_page || !pageInfo.end_cursor) return;
    setCursorStack((s) => [...s, pageInfo.end_cursor || null]);
    setCursor(pageInfo.end_cursor || null);
  };

  const goPrev = () => {
    if (cursorStack.length <= 1) return;
    const next = cursorStack.slice(0, -1);
    setCursorStack(next);
    setCursor(next[next.length - 1]);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0">
          <button type="button" onClick={toggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-lg bg-green-50 text-green-700 flex items-center justify-center shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-gray-900 truncate">{t('shopify.importTitle')}</h1>
            <p className="text-sm text-gray-500 truncate">{t('shopify.importSubtitle')}</p>
          </div>
          <Link to="/settings?tab=marketplaces&configure=shopify" className="btn-secondary text-sm hidden sm:inline-flex items-center gap-1.5">
            <ExternalLink className="w-4 h-4" /> {t('shopify.settingsLink')}
          </Link>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {statusLoading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : !connected ? (
            <div className="card p-6 max-w-xl space-y-3">
              <p className="text-sm text-gray-700">{t('shopify.importNeedConnection')}</p>
              <Link to="/settings?tab=marketplaces&configure=shopify" className="btn-primary inline-flex items-center gap-2">
                {t('shopify.goToSettings')}
              </Link>
            </div>
          ) : (
            <>
              <div className="card p-4 space-y-3">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="min-w-[160px] flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('shopify.importCollection')}</label>
                    <select
                      className="input-field w-full"
                      value={collectionId}
                      onChange={(e) => {
                        setCollectionId(e.target.value);
                        setCursor(null);
                        setCursorStack([null]);
                      }}
                      disabled={colsLoading}
                    >
                      <option value="">{t('shopify.importAllCollections')}</option>
                      {(collectionsData || []).map((c) => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[200px] flex-[2]">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.search')}</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          className="input-field w-full pl-9"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                          placeholder={t('shopify.importSearchPlaceholder')}
                        />
                      </div>
                      <button type="button" className="btn-secondary" onClick={applySearch}>
                        {t('common.search')}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary inline-flex items-center gap-2"
                    onClick={() => refetch()}
                    disabled={isFetching}
                  >
                    {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {t('shopify.importFetch')}
                  </button>
                </div>

                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded accent-green-600" checked={notInPos} onChange={(e) => { setNotInPos(e.target.checked); setCursor(null); setCursorStack([null]); }} />
                    {t('shopify.importNotInPos')}
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded accent-green-600" checked={importCategories} onChange={(e) => setImportCategories(e.target.checked)} />
                    {t('shopify.importCategories')}
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded accent-green-600" checked={importStock} onChange={(e) => setImportStock(e.target.checked)} />
                    {t('shopify.importStock')}
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded accent-green-600" checked={skipExisting} onChange={(e) => setSkipExisting(e.target.checked)} />
                    {t('shopify.importSkipExisting')}
                  </label>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm text-gray-600">
                    {t('shopify.importSelected', { count: selectedList.length })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-secondary text-sm" onClick={toggleAllOnPage} disabled={!rows.length}>
                      {allOnPageSelected ? t('shopify.importDeselectPage') : t('shopify.importSelectPage')}
                    </button>
                    <button
                      type="button"
                      className="btn-primary inline-flex items-center gap-2 text-sm"
                      disabled={!selectedList.length || importMutation.isPending}
                      onClick={() => importMutation.mutate()}
                    >
                      {importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                      {t('shopify.importAddToPos')}
                    </button>
                  </div>
                </div>

                {productsLoading ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                  </div>
                ) : productsError ? (
                  <div className="p-8 text-center text-sm text-red-600">
                    {(productsErr as any)?.response?.data?.message
                      || (productsErr as Error)?.message
                      || t('shopify.importFailed')}
                  </div>
                ) : rows.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500 space-y-2">
                    <p>{t('shopify.importEmpty')}</p>
                    {notInPos && (
                      <p className="text-xs text-gray-400">{t('shopify.importEmptyHint')}</p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2 w-10">
                            <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage} className="rounded accent-green-600" />
                          </th>
                          <th className="px-3 py-2">{t('shopify.importColProduct')}</th>
                          <th className="px-3 py-2">{t('shopify.importColSku')}</th>
                          <th className="px-3 py-2">{t('shopify.importColCategory')}</th>
                          <th className="px-3 py-2 text-right">{t('shopify.importColPrice')}</th>
                          <th className="px-3 py-2 text-right">{t('shopify.importColQty')}</th>
                          <th className="px-3 py-2">{t('shopify.importColStatus')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map((row) => (
                          <tr key={row.variant_id} className="hover:bg-gray-50/80">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                className="rounded accent-green-600"
                                checked={!!selected[row.variant_id]}
                                onChange={() => toggleRow(row)}
                              />
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900 max-w-xs">
                              <div className="truncate">{row.title}</div>
                              {row.is_multi_variant && (
                                <span className="inline-block mt-0.5 text-[10px] font-medium text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
                                  {t('shopify.importAsVariants', { count: row.variant_count || 0 })}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{row.sku || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{row.category_hint || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{Number(row.price || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{Number(row.qty || 0)}</td>
                            <td className="px-3 py-2">
                              {row.already_linked ? (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                  <Check className="w-3 h-3" /> {t('shopify.importInPos')}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">{t('shopify.importNew')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="px-4 py-3 border-t border-gray-100 flex justify-between">
                  <button type="button" className="btn-secondary text-sm" disabled={cursorStack.length <= 1} onClick={goPrev}>
                    {t('common.previous')}
                  </button>
                  <button type="button" className="btn-secondary text-sm" disabled={!pageInfo.has_next_page} onClick={goNext}>
                    {t('common.next')}
                  </button>
                </div>
              </div>

              {resultMsg && (
                <div className="p-3 rounded-lg text-sm bg-emerald-50 text-emerald-800">{resultMsg}</div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
